import type { Route } from './+types/reviews';
import { Link } from 'react-router';
import { RelatedPages } from '~/components/related-pages';
import { SITE } from '~/data/site';
import { REVIEWS, REVIEWS_SCHEMA } from '~/data/reviews';
import { buildMeta, reviewsPageSchema, breadcrumbSchema } from '~/lib/seo';
import { useBranding, brandingFromMatches } from '~/lib/branding';

// The hardcoded REVIEWS_DATA list that used to sit here — a second copy of the
// same seven reviews, worded slightly differently from the visible quotes — is
// gone. Schema now derives from the one source, so the markup and the on-page
// text cannot disagree (Google suppresses the rich result when they do).

// The page body already hides the named Yelp testimonials from every tenant
// but New Leaf (they're real reviewers, not generic copy — see the gate
// below). This meta export did NOT match that gate: it emitted the same
// Review/AggregateRating JSON-LD — citing those same real people by name —
// into every tenant's page source regardless of branding, which is worse
// than the visible-copy version of the bug since it is machine-readable
// structured data claiming someone else's reviews.
export const meta: Route.MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  return buildMeta({
    title: 'Reviews',
    description: branding.isNewLeaf
      ? '5 stars on Yelp. Real clients, real visits. New Leaf Cleaning — Greater Tampa Bay.'
      : `Client reviews for ${branding.name}.`,
    path: '/reviews',
    siteName: branding.name,
    jsonLd: branding.isNewLeaf
      ? reviewsPageSchema(REVIEWS_SCHEMA, [
          { name: 'Home', url: `${SITE.url}/` },
          { name: 'Reviews', url: `${SITE.url}/reviews` },
        ])
      : breadcrumbSchema([
          { name: 'Home', url: `${SITE.url}/` },
          { name: 'Reviews', url: `${SITE.url}/reviews` },
        ]),
  });
};

export default function ReviewsPage() {
  const branding = useBranding();
  return (
    <main>
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <p className="eyebrow">What our neighbors say</p>
          <h1 className="display-lg page-h1">
            5 stars on Yelp.{' '}
            <em className="italic-display">Every one from a real visit.</em>
          </h1>
          <p className="lede chapter-lede">
            We don&rsquo;t solicit from friends. We don&rsquo;t offer discounts for reviews.
            These are clients writing after working with us.
          </p>

          {/* Stat cards */}
          <div className="stat-cards">
            <div className="stat-card reveal">
              <div className="stat-number">5.0</div>
              <div className="testimonial-stars">★★★★★</div>
              <div className="eyebrow stat-label">Yelp rating</div>
              <div className="stat-meta">Yelp</div>
            </div>
            <div className="stat-card reveal">
              <div className="stat-number">Dec</div>
              <div className="eyebrow stat-label stat-label-mt">
                St. Petersburg since
              </div>
              <div className="stat-meta">
                2025
              </div>
            </div>
            <div className="stat-card reveal">
              <div className="stat-number">W-2</div>
              <div className="eyebrow stat-label stat-label-mt">
                Employee model
              </div>
              <div className="stat-meta">
                No contractors, ever
              </div>
            </div>
          </div>
        </div>
      </section>

      {/*
        These are New Leaf's real Yelp reviewers, by name. On another tenant's
        domain they would be that company presenting someone else's customers as
        its own — a fabricated testimonial, not a branding slip. So the wall
        renders only for New Leaf; everyone else gets an honest empty state
        until they have reviews of their own.
      */}
      {branding.isNewLeaf ? (<>
      {/* ── REVIEWS ──────────────────────────────────────────
          Rebuilt 2026-09-10. Was seven identical bordered cards in a flat grid,
          all set in display italic — a type style Bricolage Grotesque does not
          have. Now a masonry-ish column flow with a real pull-quote lead, an
          oversized brass quote mark, and alternating card treatments so the page
          reads as a wall of voices rather than a spreadsheet.
          All seven come from the ONE source, src/app/data/reviews.ts. */}
      <section className="rv-wall">
        <div className="container">
          <div className="rv-lead">
            <div className="rv-lead__mark" aria-hidden="true">&ldquo;</div>
            <blockquote className="rv-lead__quote">{REVIEWS[0].short}</blockquote>
            <div className="rv-lead__by">
              <span className="rv-lead__name">{REVIEWS[0].name}</span>
              <span className="rv-lead__meta">{REVIEWS[0].location} &middot; Yelp &middot; {REVIEWS[0].date}</span>
            </div>
          </div>

          <div className="rv-grid">
            {REVIEWS.slice(1).map((r, i) => (
              <article className={`rv-card reveal${i % 3 === 1 ? ' rv-card--tint' : ''}`} key={r.name}>
                <div className="rv-card__stars" role="img" aria-label="5 out of 5 stars">
                  {'\u2605'.repeat(5)}
                </div>
                <blockquote className="rv-card__quote">{r.quote}</blockquote>
                <footer className="rv-card__by">
                  <span className="rv-card__name">{r.name}</span>
                  <span className="rv-card__meta">{r.location} &middot; Yelp &middot; {r.date}</span>
                </footer>
              </article>
            ))}
          </div>

          <div className="section-cta">
            <a
              href={SITE.reviews.yelpUrl}
              className="btn btn-ghost"
              target="_blank"
              rel="noopener noreferrer"
            >
              See all reviews on Yelp &rarr;
            </a>
          </div>
        </div>
      </section>
      </>) : (
        <section className="section-sm bg-bone-warm">
          <div className="container">
            <p className="lede">No reviews published yet.</p>
          </div>
        </section>
      )}

      {/* ── CTA ──────────────────────────────────────────── */}
      <RelatedPages path="/reviews" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready?</p>
          <h2>See if it&rsquo;s <em>the right fit</em>.</h2>
          <p className="cta-lede">Ninety seconds, your exact price, no pressure.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">See your price →</Link>
        </div>
      </section>
    </main>
  );
}
